import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Slider,
    Snackbar,
    Typography
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';

import { t } from 'i18next';
import Logger from '../../../utils/logger';
import { fetchUser, getUserGroups } from '../../../services/users';
import { getPatientPlans } from '../../../services/patient';
import { fetchWeeklyComputations } from '../../../services/computations';
import { useRole } from '../../../utils/RoleContext';
import { usePatientQuestionnaireAnswers } from '../../../hooks/usePatientQuestionnaireAnswers';
import { usePatientPendingAnswers } from '../../../hooks/usePatientPendingAnswers';

import ConsultationCard from '../Consultation/ConsultationCard';
import ScheduleConsultationDialog from '../Consultation/ScheduleConsultationDialog';
import { fetchAppointments } from '../../../services/appointments';
import { getPatientNutritionPlans } from '../../../services/nutrition';
import { getPanelRole } from '../../../services/api';
import AddMedication from '../medication';
import Records from './records';
import UserStats from './components/UserStats';
import WeeklyProgressChart from './components/WeeklyProgressChart';
import ExerciseRatioPie from './components/ExerciseRatioPie';
import ExerciseTypesPie from './components/ExerciseTypesPie';
import PendingQuestionnaires from './components/PendingQuestionnaires';
import AnsweredQuestionnaires from './components/AnsweredQuestionnaires';
import PatientPlanTimeline from './components/PatientPlanTimeline';
import StressTestsSection from './components/StressTestsSection';
import PatientHeader from './components/PatientHeader';
import PatientOverview from './components/PatientOverview';
import PatientSectionHeading from './components/PatientSectionHeading';
import PatientMeasurements from './components/PatientMeasurements';
import NewClinicalMeasurementDialog from './components/NewClinicalMeasurementDialog';
import PatientAlerts from './components/PatientAlerts';
import AssignPatientPlanDialog from './components/AssignPatientPlanDialog';
import AssignPatientGroupDialog from './components/AssignPatientGroupDialog';
import AssignPatientNutritionDialog from './components/AssignPatientNutritionDialog';

const SECTION_PATHS = [
    '',
    'consultas',
    'exames',
    'medicacao',
    'atividade',
    'questionarios',
    'medicoes',
    'alertas'
];

const getSectionIndex = (section) => {
    const index = SECTION_PATHS.indexOf(section || '');
    return index === -1 ? 0 : index;
};

const buildPatientRoute = (patientUuid, section = '') =>
    `/users/user/${patientUuid}${section ? `/${section}` : ''}`;

const SeeUser = () => {
    const location = useLocation();
    const { state } = location;
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { patientUuid, patientSection } = useParams();
    const restoredState = state?.restoredState;
    const stateUser = state?.user;
    const roleUuid = patientUuid || stateUser?.role_uuid || '';
    const selected = getSectionIndex(patientSection);
    const { role } = useRole();
    const [newMeasurementOpen, setNewMeasurementOpen] = useState(false);

    const [groups, setGroups] = useState([]);
    const [userData, setUserData] = useState([]);
    const [plans, setPlans] = useState([]);
    const [patientContextLoading, setPatientContextLoading] = useState(true);
    const [patientContextError, setPatientContextError] = useState(false);
    const [weeklyDataList, setWeeklyDataList] = useState([]);
    const [scheduledConsultations, setScheduledConsultations] = useState([]);
    const [completedConsultations, setCompletedConsultations] = useState([]);
    const [consultationsLoading, setConsultationsLoading] = useState(false);
    const [consultationsError, setConsultationsError] = useState(false);
    const [consultationsVersion, setConsultationsVersion] = useState(0);
    const [assignPlanDialogOpen, setAssignPlanDialogOpen] = useState(false);
    const [assignGroupDialogOpen, setAssignGroupDialogOpen] = useState(false);
    const [assignNutritionDialogOpen, setAssignNutritionDialogOpen] = useState(false);
    const canAssignNutrition = role === 'DOCTOR';
    const nutritionQueryKey = ['patient-nutrition-plans', getPanelRole(), roleUuid];
    const nutritionQuery = useQuery({
        queryKey: nutritionQueryKey,
        queryFn: ({ signal }) => getPatientNutritionPlans(roleUuid, signal),
        enabled: Boolean(roleUuid),
        staleTime: 60_000
    });
    const [assignmentFeedback, setAssignmentFeedback] = useState('');

    const sections = [
        t('userPage.sections.general'),
        t('userPage.sections.appointment'),
        t('userPage.sections.exams'),
        t('userPage.sections.medication'),
        t('userPage.sections.activity'),
        t('userPage.sections.questionnaires'),
        t('userPage.sections.measurements'),
        t('userPage.sections.alerts')
    ];
    const [selectedPlan, setSelectedPlan] = useState(null);
    const selectedPlanUuid = selectedPlan?.uuid || null;
    const selectedPlanStart = selectedPlan?.start_date || null;
    const selectedPlanEnd = selectedPlan?.end_date || null;
    const [numWeeks, setNumWeeks] = useState(4);

    const totalExercises = weeklyDataList.reduce(
        (sum, week) =>
            sum +
            Object.values(week.data.exercises?.done_in_plan || {}).reduce(
                (a, b) => a + b,
                0
            ),
        0
    );

    const totalSkipped = weeklyDataList.reduce(
        (sum, week) =>
            sum +
            Object.values(week.data.exercises?.pending_in_plan || {}).reduce(
                (a, b) => a + b,
                0
            ),
        0
    );

    const aggregatedTypes = {};
    weeklyDataList.forEach((week) => {
        const total = week.data.exercises?.total || {};
        Object.entries(total).forEach(([type, count]) => {
            if (!aggregatedTypes[type]) aggregatedTypes[type] = 0;
            aggregatedTypes[type] += count;
        });
    });
    const exerciseTypes = Object.entries(aggregatedTypes).map(([name, value]) => ({
        name,
        value
    }));

    const weeklyStats = weeklyDataList.map((week) => {
        const done = Object.values(week.data.exercises?.done_in_plan || {}).reduce(
            (a, b) => a + b,
            0
        );
        const skipped = Object.values(week.data.exercises?.pending_in_plan || {}).reduce(
            (a, b) => a + b,
            0
        );
        return { week: week.week, done, skipped };
    });

    const ratio =
        totalSkipped === 0
            ? '0'
            : `${Math.round((totalExercises / (totalExercises + totalSkipped)) * 100)}%`;

    const {
        data: questionnaireAnswers = [],
        isLoading: qaLoading,
        count: qaTotalCount,
        paginationModel: qaPaginationModel,
        setPaginationModel: setQaPaginationModel,
        rowsPerPageOptions: qaRowsPerPageOptions
    } = usePatientQuestionnaireAnswers(roleUuid, selected === 5);

    const {
        data: pendingAnswers = [],
        count: pendingTotalCount = 0,
        paginationModel: pendingPaginationModel = { page: 0, pageSize: 10 },
        setPaginationModel: setPendingPaginationModel = () => {}
    } = usePatientPendingAnswers(roleUuid, selected === 5);

    const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

    const navigateToSection = (section = '') =>
        navigate(buildPatientRoute(roleUuid, section));

    useEffect(() => {
        if (restoredState?.selectedPlan) setSelectedPlan(restoredState.selectedPlan);
    }, [restoredState]);

    useEffect(() => {
        if (patientUuid || !roleUuid) return;

        const restoredSection = SECTION_PATHS[restoredState?.selected] || '';
        navigate(buildPatientRoute(roleUuid, restoredSection), {
            replace: true,
            state: { ...state, user: stateUser }
        });
    }, [navigate, patientUuid, restoredState?.selected, roleUuid, state, stateUser]);

    const seeUserState = React.useMemo(
        () => ({
            selected,
            expanded: false,
            selectedPlan
        }),
        [selected, selectedPlan]
    );

    const renderConsultationGrid = (consultations, onCardClick) => (
        <Grid container spacing={2}>
            {consultations.map((consult) => (
                <Grid item xs={12} md={6} lg={4} key={consult.uuid}>
                    <ConsultationCard
                        appointment={consult}
                        onClick={() => onCardClick(consult)}
                    />
                </Grid>
            ))}
        </Grid>
    );

    const loadPatientContext = useCallback(async () => {
        if (!roleUuid) return;

        setPatientContextLoading(true);
        setPatientContextError(false);
        try {
            const [userResp, groupsResp, plansResp] = await Promise.all([
                fetchUser(roleUuid),
                getUserGroups(roleUuid),
                getPatientPlans(roleUuid)
            ]);
            setUserData(userResp);
            setGroups(groupsResp);
            setPlans(plansResp);
        } catch (error) {
            setPatientContextError(true);
            Logger.error('Erro ao procurar dados do paciente:', error);
        } finally {
            setPatientContextLoading(false);
        }
    }, [roleUuid]);

    useEffect(() => {
        loadPatientContext();
    }, [loadPatientContext]);

    const handlePlanAssigned = async () => {
        try {
            setPlans(await getPatientPlans(roleUuid));
        } catch (error) {
            Logger.error('Erro ao atualizar os planos do paciente:', error);
        }
        setAssignmentFeedback(t('userPage.overview.assignments.planAssignedSuccess'));
    };

    const handleGroupAssigned = async () => {
        try {
            setGroups(await getUserGroups(roleUuid));
        } catch (error) {
            Logger.error('Erro ao atualizar os grupos do paciente:', error);
        }
        setAssignmentFeedback(t('userPage.overview.assignments.groupAssignedSuccess'));
    };

    useEffect(() => {
        const fetchWeeklyData = async () => {
            if (selected !== 4 || !roleUuid) return;

            try {
                let weeks = [];

                if (selectedPlan) {
                    const start = dayjs(selectedPlan.start_date);
                    const end = dayjs(selectedPlan.end_date);

                    const numWeeks = Math.ceil(end.diff(start, 'week', true));
                    weeks = [...Array(numWeeks)].map((_, i) =>
                        start.add(i, 'week').startOf('week').format('YYYY-MM-DD')
                    );
                } else {
                    const today = dayjs();
                    weeks = [...Array(numWeeks)]
                        .map((_, i) =>
                            today.subtract(i, 'week').startOf('week').format('YYYY-MM-DD')
                        )
                        .reverse();
                }

                const results = await Promise.all(
                    weeks.map((weekStart) =>
                        fetchWeeklyComputations({
                            qi: weekStart,
                            patient: roleUuid,
                            plan: selectedPlanUuid || undefined
                        }).then((data) => ({
                            week: dayjs(weekStart).format('DD-MM'),
                            data
                        }))
                    )
                );

                setWeeklyDataList(results);
            } catch (err) {
                Logger.error('Erro ao procurar dados das semanas:', err);
            }
        };

        fetchWeeklyData();
    }, [roleUuid, selected, selectedPlan, selectedPlanUuid, numWeeks]);

    useEffect(() => {
        if (selected !== 1 || !roleUuid) return;
        let active = true;

        const loadAppointments = async () => {
            setConsultationsLoading(true);
            setConsultationsError(false);
            setScheduledConsultations([]);
            setCompletedConsultations([]);
            try {
                const scheduled = await fetchAppointments({
                    patient: roleUuid,
                    statuses: ['SCHEDULED', 'ONGOING'],
                    order: 'ASC',
                    allPages: true
                });

                const completed = await fetchAppointments({
                    patient: roleUuid,
                    statuses: ['COMPLETED'],
                    order: 'DESC',
                    allPages: true
                });

                if (active) {
                    setScheduledConsultations(scheduled);
                    setCompletedConsultations(completed);
                }
            } catch (err) {
                Logger.error(err);
                if (active) setConsultationsError(true);
            } finally {
                if (active) setConsultationsLoading(false);
            }
        };

        loadAppointments();
        return () => {
            active = false;
        };
    }, [roleUuid, selected, consultationsVersion]);

    if (!roleUuid) {
        return (
            <Box p={2}>
                <Typography color="error">{t('userPage.invalidUser')}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, overflowX: 'auto' }}>
            <Box sx={{ minWidth: 1040 }}>
                <PatientHeader
                    patient={userData.data || stateUser || {}}
                    patientUuid={roleUuid}
                    plans={plans}
                    groups={groups}
                    contextLoading={patientContextLoading}
                    contextError={patientContextError}
                    onEdit={() =>
                        navigate('/users/user/edit', {
                            state: { user: { role_uuid: roleUuid } }
                        })
                    }
                    onAddStressTest={() =>
                        navigate('/users/user/stress-test/create', {
                            state: {
                                patient: userData.data || stateUser,
                                from: location,
                                seeUserState
                            }
                        })
                    }
                    onOpenMessages={() => navigate(`/mensagens/${roleUuid}`)}
                    onAssignPlan={() => setAssignPlanDialogOpen(true)}
                    onAssignGroup={() => setAssignGroupDialogOpen(true)}
                    nutritionPlans={nutritionQuery.data || []}
                    nutritionLoading={nutritionQuery.isPending}
                    nutritionError={nutritionQuery.isError}
                    onAssignNutrition={
                        canAssignNutrition
                            ? () => setAssignNutritionDialogOpen(true)
                            : undefined
                    }
                />

                <PatientSectionHeading
                    sectionLabel={selected === 0 ? null : sections[selected]}
                    onBack={() => navigateToSection()}
                    action={
                        selected === 6 &&
                        ['DOCTOR', 'CLINIC_CARE_GIVER'].includes(role) ? (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setNewMeasurementOpen(true)}
                                sx={{
                                    height: 46,
                                    borderRadius: 1.5,
                                    fontWeight: 700,
                                    textTransform: 'none'
                                }}
                            >
                                {t('userPage.measurements.newMeasurement')}
                            </Button>
                        ) : null
                    }
                />

                {selected === 0 && (
                    <PatientOverview
                        patientUuid={roleUuid}
                        patientCreated={(userData.data || stateUser)?.created}
                        plans={plans}
                        plansLoading={patientContextLoading}
                        plansError={patientContextError}
                        onNavigate={navigateToSection}
                        onScheduleAppointment={() => setScheduleDialogOpen(true)}
                        onAddMeasurement={
                            ['DOCTOR', 'CLINIC_CARE_GIVER'].includes(role)
                                ? () => setNewMeasurementOpen(true)
                                : undefined
                        }
                        onAddStressTest={() =>
                            navigate('/users/user/stress-test/create', {
                                state: {
                                    patient: userData.data || stateUser,
                                    from: location,
                                    seeUserState
                                }
                            })
                        }
                    />
                )}
                {selected === 1 && (
                    <>
                        {consultationsLoading && (
                            <Typography sx={{ mt: 2 }}>
                                A carregar consultas...
                            </Typography>
                        )}
                        {consultationsError && (
                            <Alert
                                severity="error"
                                sx={{ mt: 2 }}
                                action={
                                    <Button
                                        color="inherit"
                                        onClick={() =>
                                            setConsultationsVersion((value) => value + 1)
                                        }
                                    >
                                        Tentar novamente
                                    </Button>
                                }
                            >
                                Não foi possível carregar as consultas.
                            </Alert>
                        )}
                        <Box sx={{ mt: 2 }}>
                            <Card>
                                <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                                    {/* header */}
                                    <Box
                                        display="flex"
                                        justifyContent="space-between"
                                        mb={2}
                                    >
                                        <Typography variant="h5" fontWeight={700}>
                                            {t('userPage.completed_consultations')}
                                        </Typography>
                                    </Box>

                                    {!consultationsLoading &&
                                        !consultationsError &&
                                        completedConsultations.length === 0 && (
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {t('userPage.noConsultation')}
                                                </Typography>
                                            </Box>
                                        )}

                                    {/* Realizadas */}
                                    {completedConsultations.length > 0 &&
                                        renderConsultationGrid(
                                            completedConsultations,
                                            (consult) =>
                                                navigate(
                                                    `/users/user/consultation/${consult.uuid}`,
                                                    {
                                                        state: {
                                                            from: location,
                                                            seeUserState
                                                        }
                                                    }
                                                )
                                        )}
                                </CardContent>
                            </Card>
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <Card sx={{ mb: 3 }}>
                                <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                                    {/* header */}
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography variant="h5" fontWeight={700}>
                                            {t('userPage.scheduled_consultations')}
                                        </Typography>
                                        <Box display="flex" gap={2}>
                                            <Button
                                                variant="outlined"
                                                onClick={() =>
                                                    setScheduleDialogOpen(true)
                                                }
                                            >
                                                {t('userPage.create_consultation')}
                                            </Button>
                                        </Box>
                                    </Box>

                                    {!consultationsLoading &&
                                        !consultationsError &&
                                        scheduledConsultations.length === 0 && (
                                            <Box
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                            >
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                >
                                                    {t('userPage.noScheduleConsultation')}
                                                </Typography>
                                            </Box>
                                        )}

                                    {/* Agendadas */}
                                    {scheduledConsultations.length > 0 &&
                                        renderConsultationGrid(
                                            scheduledConsultations,
                                            (consult) =>
                                                navigate(
                                                    '/users/user/consultation/create',
                                                    {
                                                        state: {
                                                            patient: userData.data,
                                                            appointmentUuid: consult.uuid,
                                                            from: {
                                                                pathname:
                                                                    location.pathname,
                                                                search: location.search
                                                            },
                                                            seeUserState
                                                        }
                                                    }
                                                )
                                        )}
                                </CardContent>
                            </Card>
                        </Box>
                    </>
                )}
                {selected === 2 && (
                    <StressTestsSection
                        user={userData.data || { role_uuid: roleUuid }}
                        seeUserState={seeUserState}
                    />
                )}

                {selected === 3 && <AddMedication patientUUID={roleUuid} />}

                {selected === 4 && (
                    <>
                        <Card sx={{ mb: 2 }}>
                            <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                                <Typography variant="h5" fontWeight={700} gutterBottom>
                                    {t('userPage.generalData')}
                                </Typography>
                                <Box
                                    sx={{
                                        height: 3,
                                        width: 95,
                                        backgroundColor: '#1976d2',
                                        borderRadius: 2,
                                        mb: 2
                                    }}
                                />
                                <UserStats
                                    groups={groups}
                                    plans={plans}
                                    totalExercises={totalExercises}
                                    totalSkipped={totalSkipped}
                                    onSelectPlan={setSelectedPlan}
                                    selectedPlan={selectedPlan}
                                />
                            </CardContent>
                        </Card>

                        <Grid container spacing={2} sx={{ mb: 3 }}>
                            <Grid item xs={12} md={6}>
                                <WeeklyProgressChart weeklyStats={weeklyStats} />
                                {!selectedPlanUuid && (
                                    <Box sx={{ ml: 3, px: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            {t('userPage.weeksToView', { numWeeks })}
                                        </Typography>
                                        <Slider
                                            value={numWeeks}
                                            onChangeCommitted={(_, newValue) =>
                                                setNumWeeks(newValue)
                                            }
                                            min={4}
                                            max={52}
                                            step={1}
                                            marks={[
                                                { value: 4, label: '4' },
                                                { value: 12, label: '12' },
                                                { value: 26, label: '26' },
                                                { value: 52, label: '52' }
                                            ]}
                                            valueLabelDisplay="auto"
                                            sx={{ width: '100%', mx: 'auto' }}
                                        />
                                    </Box>
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <ExerciseRatioPie
                                            totalExercises={totalExercises}
                                            totalSkipped={totalSkipped}
                                            ratio={ratio}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <ExerciseTypesPie exerciseTypes={exerciseTypes} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        {/**{selectedPlan && (
                        <CardiovascularHealthSection
                            planStart="2023-09-29"
                            planEnd="2023-12-15"
                        />
                    )}**/}
                        {selectedPlan && (
                            <PatientPlanTimeline
                                patient={roleUuid}
                                planUuid={selectedPlanUuid}
                                startData={selectedPlanStart}
                                endData={selectedPlanEnd}
                                selected={selected}
                                expanded={false}
                                selectedPlan={selectedPlan}
                            />
                        )}
                        <Card>
                            <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                                <Typography variant="h5" fontWeight={700}>
                                    {t('userPage.exercises')}
                                </Typography>
                                <Box
                                    sx={{
                                        height: 3,
                                        width: 75,
                                        backgroundColor: '#1976d2',
                                        borderRadius: 2,
                                        mb: 2
                                    }}
                                />
                                <Records
                                    patient={roleUuid}
                                    planUuid={selectedPlanUuid}
                                    seeUserState={seeUserState}
                                />
                            </CardContent>
                        </Card>
                    </>
                )}

                {selected === 5 && (
                    <>
                        {/**<IpaqScoresChart />**/}
                        <PendingQuestionnaires
                            pendingAnswers={pendingAnswers}
                            pendingTotalCount={pendingTotalCount}
                            pendingPaginationModel={pendingPaginationModel}
                            setPendingPaginationModel={setPendingPaginationModel}
                        />
                        <AnsweredQuestionnaires
                            questionnaireAnswers={questionnaireAnswers}
                            qaLoading={qaLoading}
                            qaTotalCount={qaTotalCount}
                            qaPaginationModel={qaPaginationModel}
                            setQaPaginationModel={setQaPaginationModel}
                            qaRowsPerPageOptions={qaRowsPerPageOptions}
                        />
                    </>
                )}
                {selected === 6 && <PatientMeasurements patientUuid={roleUuid} />}
                {[0, 6].includes(selected) &&
                    newMeasurementOpen &&
                    ['DOCTOR', 'CLINIC_CARE_GIVER'].includes(role) && (
                        <NewClinicalMeasurementDialog
                            key={roleUuid}
                            patientUuid={roleUuid}
                            patientName={(userData.data || stateUser)?.name}
                            onClose={() => setNewMeasurementOpen(false)}
                        />
                    )}
                {selected === 7 && <PatientAlerts patientUuid={roleUuid} />}

                <ScheduleConsultationDialog
                    open={scheduleDialogOpen}
                    onClose={() => setScheduleDialogOpen(false)}
                    patient={userData.data || stateUser || { role_uuid: roleUuid }}
                    onScheduled={() => {
                        setConsultationsVersion((value) => value + 1);
                        queryClient.invalidateQueries({
                            queryKey: ['patient-header', roleUuid]
                        });
                        queryClient.invalidateQueries({
                            queryKey: ['patient-appointments', roleUuid]
                        });
                    }}
                />

                <AssignPatientPlanDialog
                    open={assignPlanDialogOpen}
                    onClose={() => setAssignPlanDialogOpen(false)}
                    patientUuid={roleUuid}
                    patientName={(userData.data || stateUser)?.name}
                    onAssigned={handlePlanAssigned}
                />
                <AssignPatientGroupDialog
                    open={assignGroupDialogOpen}
                    onClose={() => setAssignGroupDialogOpen(false)}
                    patientUuid={roleUuid}
                    patientName={(userData.data || stateUser)?.name}
                    onAssigned={handleGroupAssigned}
                />
                {canAssignNutrition && (
                    <AssignPatientNutritionDialog
                        key={roleUuid}
                        open={assignNutritionDialogOpen}
                        onClose={() => setAssignNutritionDialogOpen(false)}
                        patientUuid={roleUuid}
                        patientName={(userData.data || stateUser)?.name}
                        onAssigned={(updated) => {
                            queryClient.setQueryData(nutritionQueryKey, updated);
                            setAssignmentFeedback(
                                t(
                                    'userPage.overview.assignments.nutritionAssignedSuccess'
                                )
                            );
                        }}
                    />
                )}
                <Snackbar
                    open={Boolean(assignmentFeedback)}
                    autoHideDuration={4500}
                    onClose={() => setAssignmentFeedback('')}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        severity="success"
                        variant="filled"
                        onClose={() => setAssignmentFeedback('')}
                    >
                        {assignmentFeedback}
                    </Alert>
                </Snackbar>
            </Box>
        </Box>
    );
};

export default SeeUser;
